import { Inject, Injectable } from '@angular/core';
import { VersionCheckConfig } from 'ag-common-lib/public-api';
import { FIREBASE_APP, VersionCheckService } from 'ag-common-svc/public-api';
import { FirebaseApp } from 'firebase/app';
import { environment } from 'src/environments/environment';
import { getFirebasePreviewChannelId, isFirebasePreviewHost } from '../utils/firebase-preview-host.util';

/**
 * "New version available" prompt for the CRM.
 *
 * Aligned with ag-portal's AppPlatformService: the shared VersionCheckService in
 * ag-common-svc does the work, and this subclass only supplies configuration. The
 * shared service listens to a Firestore document with onSnapshot, so a deploy is
 * picked up in real time instead of by polling, and falls back to polling
 * /version.json when that document is missing or the listener fails.
 *
 * This replaced a local implementation that only ever fetched /version.json on a
 * timer. That file was never produced by any build script here, so the check
 * silently did nothing — see the build-* scripts in package.json, which now run
 * ag-common-svc/scripts/write-version.js.
 *
 * Unlike ag-portal there is no onVersionAccepted override, because this app
 * registers no service worker: the plain reload the base class does is already
 * fetched from the network, so there are no worker caches to clear first.
 */
@Injectable({ providedIn: 'root' })
export class VersionUpdateService extends VersionCheckService {
  constructor(@Inject(FIREBASE_APP) firebaseApp: FirebaseApp) {
    super(firebaseApp);
  }

  start(): void {
    this.startVersionChecks(this.getVersionCheckConfig());
  }

  /**
   * Preview channels get their own document and their own storage keys. A preview
   * build carries a different version from live, so sharing either would leave the
   * two telling each other they are out of date.
   */
  private getVersionCheckConfig(): VersionCheckConfig {
    const versionSignal = environment?.versionSignal;
    const isPreviewHost = isFirebasePreviewHost();
    const previewChannelId = getFirebasePreviewChannelId();
    const baseDocumentId = versionSignal?.firestoreDocumentId ?? 'ag-crm-version';
    const baseStorageKey = versionSignal?.storageKey ?? 'ag_crm_web_version';
    const baseSnoozeStorageKey = versionSignal?.snoozeStorageKey ?? 'ag_crm_web_version_snooze_until';
    const previewSuffix = previewChannelId ? `_preview_${previewChannelId}` : '_preview';

    return {
      enabled: versionSignal?.enabled ?? true,
      firestoreCollection: versionSignal?.firestoreCollection ?? 'system',
      firestoreDocumentId:
        isPreviewHost && previewChannelId ? `${baseDocumentId}-preview-${previewChannelId}` : baseDocumentId,
      storageKey: isPreviewHost ? `${baseStorageKey}${previewSuffix}` : baseStorageKey,
      snoozeStorageKey: isPreviewHost ? `${baseSnoozeStorageKey}${previewSuffix}` : baseSnoozeStorageKey,
      fallbackPollMs: versionSignal?.fallbackPollMs ?? 1000 * 60,
      afterDeclinePollMs: versionSignal?.afterDeclinePollMs ?? 1000 * 60,
    };
  }
}
